import React, { useState, useEffect } from "react";
import { queueAndFlush, flushQueue, queueCount, getQueuedDeletedIds } from "@/lib/syncQueue";

const SWITCH_CLEAR_KEYS = ['orders_cache', 'lumber_cache', 'lumber_history_cache', 'order_history_cache', 'print_history_cache', 'cut_history_cache', 'pending_item_updates', 'pending_order_updates', 'pending_reorder', 'savedList_local'];
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { User, LogOut, Save, UserPlus, KeyRound, Eye, EyeOff, ChevronRight, Mail, Pen, Printer, Trash2, Pencil, HelpCircle, AlertTriangle, Menu, Shield, Monitor, FileText, Sparkles, Check, Layers, Highlighter, CreditCard, Image as ImageIcon, ClipboardList, Plus, X, History, Bell, MoreVertical, CalendarDays } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { enablePush, disablePush, setOrderAlerts } from "@/lib/push";
import { toast } from "sonner";
import axios from "axios";
import { saveTokens, getAccessToken, getRefreshToken, clearTokens } from "@/lib/api";
import { CutListItem } from "./CutListItem";
import { fuzzyScore } from "./PalletTypesSidebar";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function AccountButton({ user, handleLogout, onUserUpdate, onStartTutorial, onShowPrivacy, onShowTerms, onShowMonthlyHistory }) {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  // Name
  const [name, setName] = useState("");
  // Email
  const [email, setEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  // Password
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // Switch
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [switchEmail, setSwitchEmail] = useState("");
  const [switchPassword, setSwitchPassword] = useState("");
  const [switchMode, setSwitchMode] = useState("login"); // "login" or "register"
  const [switchName, setSwitchName] = useState("");
  // Print History
  const [printHistory, setPrintHistory] = useState([]);
  const [deletePrintId, setDeletePrintId] = useState(null);
  // Cut List History
  const [cutHistory, setCutHistory] = useState([]);
  const [cutHistSearch, setCutHistSearch] = useState("");
  const [deleteCutHistId, setDeleteCutHistId] = useState(null);
  // Loading
  const [saving, setSaving] = useState(false);
  const [, setViewOnlyTick] = useState(0);

  // Admin panel
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminSelected, setAdminSelected] = useState(null);
  const [adminDevices, setAdminDevices] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminBilling, setAdminBilling] = useState(null);
  const [adminDiscount, setAdminDiscount] = useState("");
  const [adminBillingSaving, setAdminBillingSaving] = useState(false);
  const [freezeReason, setFreezeReason] = useState("");
  const [backupStatus, setBackupStatus] = useState(null);
  const [adminFinancials, setAdminFinancials] = useState(null);
  const [adminVisits, setAdminVisits] = useState(null);
  const [showEmailCompose, setShowEmailCompose] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailTargets, setEmailTargets] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const [isOwnerDevice, setIsOwnerDevice] = useState(localStorage.getItem('is_owner_device') === '1');
  const [ownerLabelUA, setOwnerLabelUA] = useState(localStorage.getItem('owner_device_ua') || '');
  const [ownerModel, setOwnerModel] = useState(localStorage.getItem('owner_device_model') || '');
  const [ownerDevices, setOwnerDevices] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(false);
  const [ownerSelected, setOwnerSelected] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    const h = () => {
      setIsOwnerDevice(localStorage.getItem('is_owner_device') === '1');
      setOwnerLabelUA(localStorage.getItem('owner_device_ua') || '');
      setOwnerModel(localStorage.getItem('owner_device_model') || '');
    };
    window.addEventListener('owner-changed', h);
    return () => window.removeEventListener('owner-changed', h);
  }, []);
  // Own subscription
  const [mySub, setMySub] = useState(null);
  const [mySubHistory, setMySubHistory] = useState(null);
  const [subCancelConfirm, setSubCancelConfirm] = useState(false);
  // Cut lists management
  const [clLists, setClLists] = useState(null);
  const [clNewName, setClNewName] = useState("");
  const [clRenameId, setClRenameId] = useState(null);
  const [clRenameVal, setClRenameVal] = useState("");
  const [clDeleteId, setClDeleteId] = useState(null);
  const [clDisplay, setClDisplay] = useState(localStorage.getItem('active_cut_list') || 'all');

  const loadCutLists = async () => {
    try { const { data } = await axios.get(`${API}/cut-lists`); setClLists(data); } catch { setClLists([]); }
  };

  const loadAdminAccounts = async () => {
    setAdminSelected(null); setAdminDevices(null); setAdminBilling(null); setAdminLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/accounts`);
      setAdminAccounts(data);
    } catch { toast.error("Failed to load accounts"); }
    try { const { data } = await axios.get(`${API}/admin/backup/status`); setBackupStatus(data.last_backup_email); } catch {}
    setAdminLoading(false);
  };

  const loadAdminDevices = async (account) => {
    setAdminSelected(account); setAdminDevices(null); setAdminBilling(null); setFreezeReason("");
    try {
      const { data } = await axios.get(`${API}/admin/accounts/${account.id}/devices`);
      setAdminDevices(data);
    } catch { toast.error("Failed to load devices"); }
    try {
      const { data } = await axios.get(`${API}/admin/accounts/${account.id}/billing`);
      setAdminBilling(data);
      setAdminDiscount(String(data.discount_percent || 0));
    } catch { toast.error("Failed to load billing info"); }
  };

  const loadFinancials = async () => {
    setAdminFinancials(null);
    try {
      const { data } = await axios.get(`${API}/admin/financials`);
      setAdminFinancials(data);
    } catch (e) { setAdminFinancials({ error: e.response?.data?.detail || "Failed to load financials" }); }
  };

  const loadVisits = async () => {
    setAdminVisits(null);
    try {
      const { data } = await axios.get(`${API}/admin/visits`);
      setAdminVisits(data);
    } catch { setAdminVisits({ total_visits: 0, unique_visitors: 0, last_30_days: [] }); }
  };

  const sendCustomerEmail = async () => {
    setEmailSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/email-customers`, { subject: emailSubject.trim(), message: emailMessage.trim(), emails: emailTargets });
      toast.success(`Email sent to ${data.sent} customer${data.sent === 1 ? '' : 's'}`);
      setShowEmailCompose(false); setEmailSubject(""); setEmailMessage("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    setEmailSending(false);
  };

  const saveAdminBilling = async (fields) => {
    setAdminBillingSaving(true);
    try {
      await axios.put(`${API}/admin/accounts/${adminSelected.id}/billing`, fields);
      const { data } = await axios.get(`${API}/admin/accounts/${adminSelected.id}/billing`);
      setAdminBilling(data);
      setAdminDiscount(String(data.discount_percent || 0));
      toast.success("Account billing updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setAdminBillingSaving(false);
  };

  const toggleFreeze = async () => {
    const next = !adminSelected.is_frozen;
    const reason = next ? freezeReason.trim() : "";
    setAdminBillingSaving(true);
    try {
      await axios.put(`${API}/admin/accounts/${adminSelected.id}/freeze`, { frozen: next, reason });
      setAdminSelected(s => ({ ...s, is_frozen: next, frozen_reason: reason }));
      setAdminAccounts(list => list.map(a => a.id === adminSelected.id ? { ...a, is_frozen: next, frozen_reason: reason } : a));
      setFreezeReason("");
      toast.success(next ? "Account frozen — user is locked out immediately" : "Account unfrozen — user can log in again");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
    setAdminBillingSaving(false);
  };

  const accessLabel = (b) => {
    if (!b) return "";
    if (b.is_panel_admin) return "Free (Admin)";
    if (b.access === "free") return "Free Access";
    if (b.access === "subscribed") return `Subscribed — ${b.plan === "yearly" ? "Yearly ($180/yr)" : "Monthly ($20/mo)"}`;
    if (b.access === "trial") return `Trial — ${b.trial_days_left} day${b.trial_days_left === 1 ? "" : "s"} left`;
    return "Unsubscribed (Locked)";
  };

  const deviceLabel = (d) => {
    const ua = d.user_agent || "";
    const chModel = (d.model || "").trim();
    let device = "";
    if (/iPhone/i.test(ua)) device = "iPhone";
    else if (/iPad/i.test(ua)) device = "iPad";
    else if (/Android/i.test(ua)) {
      const m = ua.match(/Android [\d.]+;\s*([^;)]+?)(?:\s+Build\/|\))/i);
      const uaModel = m ? m[1].trim() : "";
      const model = chModel || uaModel;
      device = model && model.toUpperCase() !== "K" ? model : "Android device";
    }
    else if (/CrOS/i.test(ua)) device = "Chromebook";
    else if (/Windows/i.test(ua)) device = "Windows PC";
    else if (/Macintosh|Mac OS/i.test(ua)) device = "Mac";
    else if (/Linux/i.test(ua)) device = "Linux PC";
    else device = chModel || "Unknown device";
    const br = /; wv\)/i.test(ua) ? "App" : /Edg\//.test(ua) ? "Edge" : /SamsungBrowser\//.test(ua) ? "Samsung Internet" : /OPR\//.test(ua) ? "Opera" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "";
    return br ? `${device} — ${br}` : device;
  };

  const deviceDetail = (d) => {
    const ua = d.user_agent || "";
    const parts = [];
    let m;
    if ((m = ua.match(/Android ([\d.]+)/i))) {
      const pv = (d.platform_version || "").split(".")[0];
      parts.push(`Android ${pv && m[1] === "10" ? pv : m[1]}`);
    }
    else if ((m = ua.match(/(?:iPhone|CPU) OS (\d+[._]\d+)/i))) parts.push(`iOS ${m[1].replace(/_/g, '.')}`);
    else if (/Windows NT 10\.0/.test(ua)) {
      const pv = parseInt((d.platform_version || "").split(".")[0] || "0", 10);
      parts.push(pv >= 13 ? "Windows 11" : pv > 0 ? "Windows 10" : "Windows 10/11");
    }
    else if ((m = ua.match(/Windows NT ([\d.]+)/))) parts.push(`Windows (NT ${m[1]})`);
    else if ((m = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/))) parts.push(`macOS ${m[1].replace(/_/g, '.')}`);
    else if (/CrOS/.test(ua)) parts.push("ChromeOS");
    else if (/Linux/.test(ua)) parts.push("Linux");
    if ((m = ua.match(/Edg\/(\d+)/))) parts.push(`Edge ${m[1]}`);
    else if ((m = ua.match(/SamsungBrowser\/([\d.]+)/))) parts.push(`Samsung Internet ${m[1]}`);
    else if ((m = ua.match(/OPR\/(\d+)/))) parts.push(`Opera ${m[1]}`);
    else if ((m = ua.match(/Firefox\/(\d+)/))) parts.push(`Firefox ${m[1]}`);
    else if ((m = ua.match(/Chrome\/(\d+)/))) parts.push(`Chrome ${m[1]}`);
    else if ((m = ua.match(/Version\/([\d.]+).*Safari/))) parts.push(`Safari ${m[1]}`);
    if (d.screen) parts.push(`${d.screen} screen`);
    return parts.join(" · ");
  };

  const myDeviceId = localStorage.getItem('device_id') || '';

  const loadOwnerDevices = async () => {
    setOwnerLoading(true);
    try {
      const { data } = await axios.get(`${API}/devices`);
      setOwnerDevices(data.devices || []);
    } catch { toast.error("Couldn't load devices"); }
    setOwnerLoading(false);
  };

  const toggleDeviceViewonly = async (dev, page, label) => {
    const cur = dev.viewonly_pages || [];
    const next = cur.includes(page) ? cur.filter(p => p !== page) : [...cur, page];
    try {
      await axios.put(`${API}/devices/viewonly`, { requester_device_id: myDeviceId, target_device_id: dev.device_id, pages: next });
      setOwnerDevices(list => list.map(d => d.device_id === dev.device_id ? { ...d, viewonly_pages: next } : d));
      setOwnerSelected(s => s && s.device_id === dev.device_id ? { ...s, viewonly_pages: next } : s);
      if (dev.device_id === myDeviceId) {
        localStorage.setItem('viewonly_pages', JSON.stringify(next));
        window.dispatchEvent(new Event('viewonly-changed'));
      }
      toast.success(`${label} is now ${next.includes(page) ? 'view-only' : 'editable'} on that device`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const doTransferOwnership = async () => {
    if (!transferTarget) return;
    setTransferring(true);
    try {
      await axios.post(`${API}/devices/transfer-ownership`, { requester_device_id: myDeviceId, new_owner_device_id: transferTarget.device_id });
      localStorage.setItem('is_owner_device', '0');
      localStorage.setItem('owner_device_ua', transferTarget.user_agent || '');
      window.dispatchEvent(new Event('owner-changed'));
      toast.success(`Ownership transferred to ${deviceLabel(transferTarget)}`);
      setTransferTarget(null);
      setOwnerSelected(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to transfer ownership"); setTransferTarget(null); }
    setTransferring(false);
  };

  const handleOpen = () => {
    setSection(null);
    setEditing(false);
    setShowAddAccount(false);
    setName(user.name || "");
    setEmail(user.email || "");
    setEmailPassword("");
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setSwitchEmail("");
    setSwitchPassword("");
    setOpen(true);
  };

  const goToSection = (s) => { setSection(s); setEditing(false); };

  const fetchPrintHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/print-history`, { timeout: 15000 });
      const deleted = getQueuedDeletedIds('print_history');
      const clean = deleted.length > 0 ? data.filter(p => !deleted.includes(p.id)) : data;
      setPrintHistory(clean);
      try { localStorage.setItem('print_history_cache', JSON.stringify(clean)); } catch {}
    } catch {
      // Offline: show the device's copy
      try {
        const cache = JSON.parse(localStorage.getItem('print_history_cache') || '[]');
        const deleted = getQueuedDeletedIds('print_history');
        setPrintHistory(cache.filter(p => !deleted.includes(p.id)));
      } catch {}
    }
  };

  const restoreCutHist = async (id) => {
    try {
      await axios.put(`${API}/saved-list/${id}`, { in_history: false, history_moved_at: null, hidden_from_monthly: false });
      setCutHistory(prev => prev.filter(x => x.id !== id));
      try { const c = JSON.parse(localStorage.getItem('cut_history_cache') || '[]'); localStorage.setItem('cut_history_cache', JSON.stringify(c.filter(x => x.id !== id))); } catch { /* ignore */ }
      try { const p = JSON.parse(localStorage.getItem('pending_item_updates') || '{}'); if (p[id]) { delete p[id].in_history; delete p[id].history_moved_at; localStorage.setItem('pending_item_updates', JSON.stringify(p)); } } catch { /* ignore */ }
      try { const cache = JSON.parse(localStorage.getItem('savedList_cache') || '{}'); if (cache.items) { cache.items = cache.items.map(x => x.id === id ? { ...x, in_history: false, history_moved_at: null } : x); localStorage.setItem('savedList_cache', JSON.stringify(cache)); } } catch { /* ignore */ }
      toast.success("Added back to cut list");
    } catch { toast.error("Couldn't add to cut list — try again"); }
  };

  const fetchCutHistory = async () => {
    // Merge in items moved to history on THIS device that haven't reached the server yet
    const buildHistory = (serverItems) => {
      const deleted = getQueuedDeletedIds('saved_list');
      let pend = {}; let cacheItems = [];
      try { pend = JSON.parse(localStorage.getItem('pending_item_updates') || '{}'); } catch {}
      try {
        const cache = JSON.parse(localStorage.getItem('savedList_cache') || '{}');
        if ((cache.email || '') === (user?.email || '')) cacheItems = cache.items || [];
      } catch {}
      const map = {};
      (serverItems || []).forEach(i => { map[i.id] = i; });
      cacheItems.forEach(i => { if (!map[i.id]) map[i.id] = i; });
      return Object.values(map)
        .map(i => (pend[i.id] ? { ...i, ...pend[i.id] } : i))
        .filter(i => i.in_history && !deleted.includes(i.id));
    };
    try {
      const { data } = await axios.get(`${API}/saved-list`, { timeout: 15000 });
      const clean = buildHistory(data);
      setCutHistory(clean);
      try { localStorage.setItem('cut_history_cache', JSON.stringify(clean)); } catch {}
    } catch {
      try {
        const cache = JSON.parse(localStorage.getItem('cut_history_cache') || '[]');
        setCutHistory(buildHistory(cache));
      } catch {}
    }
  };

  const deleteCutHistItem = async (id) => {
    // Write-ahead: recorded on device first — works offline, syncs to other devices later
    setCutHistory(prev => prev.filter(i => i.id !== id));
    queueAndFlush({ method: "delete", path: `/saved-list/${id}?restore=false`, entity: "saved_list", entityId: id });
    toast.success("Deleted");
  };

  const deletePrint = async (id) => {
    setPrintHistory(prev => prev.filter(p => p.id !== id));
    queueAndFlush({ method: "delete", path: `/print-history/${id}`, entity: "print_history", entityId: id });
    toast.success("Deleted");
  };

  const viewPrint = async (id) => {
    try { const { data } = await axios.get(`${API}/print-history/${id}`); const win = window.open('', '_blank'); if (win) { win.opener = null; win.document.write(data.html); win.document.close(); } } catch { toast.error("Failed to load"); }
  };

  const saveName = async () => {
    if (name === (user.name || "")) { toast.info("No changes"); return; }
    setSaving(true);
    try { const { data } = await axios.put(`${API}/auth/profile`, { name }); onUserUpdate(data); toast.success("Name updated!"); setEditing(false); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const saveEmail = async () => {
    if (email === (user.email || "")) { toast.info("No changes"); return; }
    if (!emailPassword) { toast.error("Enter your password to confirm"); return; }
    setSaving(true);
    try { const { data } = await axios.put(`${API}/auth/profile`, { email, password_confirm: emailPassword }); onUserUpdate(data); setEmailPassword(""); toast.success("Email updated!"); setEditing(false); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const changePassword = async () => {
    if (!currentPassword) { toast.error("Enter current password"); return; }
    if (!newPassword || newPassword.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try { const { data } = await axios.put(`${API}/auth/change-password`, { email: user.email, current_password: currentPassword, new_password: newPassword }); if (data?.access_token) saveTokens(data.access_token, data.refresh_token); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); toast.success("Password changed!"); setEditing(false); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const getSavedAccounts = () => {
    try { return JSON.parse(localStorage.getItem('saved_accounts') || '[]'); } catch { return []; }
  };

  const saveCurrentAccount = () => {
    const accounts = getSavedAccounts();
    const token = getAccessToken();
    const refreshToken = getRefreshToken();
    const existing = accounts.findIndex(a => a.email === user.email);
    const entry = { email: user.email, name: user.name || "", token, refresh_token: refreshToken };
    if (existing >= 0) accounts[existing] = entry;
    else accounts.push(entry);
    localStorage.setItem('saved_accounts', JSON.stringify(accounts));
  };

  const removeSavedAccount = (email) => {
    const accounts = getSavedAccounts().filter(a => a.email !== email);
    localStorage.setItem('saved_accounts', JSON.stringify(accounts));
  };
  const updateSavedAccount = (email, name, token, refreshToken) => {
    const accounts = getSavedAccounts();
    const existing = accounts.findIndex(a => a.email === email);
    const entry = { email, name: name || "", token, refresh_token: refreshToken };
    if (existing >= 0) accounts[existing] = entry;
    else accounts.push(entry);
    localStorage.setItem('saved_accounts', JSON.stringify(accounts));
  };

  const switchToAccount = async (account) => {
    // Push this account's unsynced changes FIRST so they can't leak into the other account
    try { await flushQueue(); } catch {}
    if (queueCount() > 0) {
      toast.error("You have unsynced changes — connect to the internet before switching accounts");
      return;
    }
    saveCurrentAccount();
    try {
      // Validate the target account's tokens with EXPLICIT headers so the old
      // account's stored token can never be silently used instead
      let access = account.token || null;
      let refresh = account.refresh_token || null;
      let data = null;
      if (access) {
        try {
          const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${access}` } });
          data = res.data;
        } catch {}
      }
      if (!data) {
        if (!refresh) throw new Error("no refresh token");
        const { data: t } = await axios.post(`${API}/auth/refresh`, { refresh_token: refresh }, { headers: { Authorization: `Bearer ${refresh}` } });
        access = t.access_token;
        refresh = t.refresh_token || refresh;
        const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${access}` } });
        data = res.data;
      }
      // Write BOTH tokens atomically — never leave the old account's refresh token behind
      localStorage.setItem("access_token", access);
      if (refresh) localStorage.setItem("refresh_token", refresh);
      else localStorage.removeItem("refresh_token");
      updateSavedAccount(data.email, data.name, access, refresh);
      // The app boots from cached_user — it MUST be the NEW account before reload,
      // and the old account's device caches must not bleed through
      try { localStorage.setItem("cached_user", JSON.stringify(data)); } catch {}
      SWITCH_CLEAR_KEYS.forEach(k => localStorage.removeItem(k));
      // Sync httpOnly cookies to the new account before reloading (image auth)
      try { await axios.post(`${API}/auth/cookie-sync`); } catch {}
      onUserUpdate(data);
      setOpen(false);
      toast.success(`Switched to ${account.email}`);
      window.location.reload();
    } catch {
      toast.error(`Please re-enter the password for ${account.email}`);
      setShowAddAccount(true);
      setSwitchMode("login");
      setSwitchEmail(account.email);
      setSwitchPassword("");
    }
  };

  const switchAccount = async () => {
    const email = switchEmail.trim().toLowerCase();
    const password = switchPassword.trim();
    if (!email || !password) { toast.error("Enter email and password"); return; }
    try { await flushQueue(); } catch {}
    if (queueCount() > 0) {
      toast.error("You have unsynced changes — connect to the internet before switching accounts");
      return;
    }
    saveCurrentAccount();
    setSaving(true);
    try {
      let data;
      if (switchMode === "register") {
        if (!switchName) { toast.error("Enter a name"); setSaving(false); return; }
        const res = await axios.post(`${API}/auth/register`, { email, password, name: switchName, remember_me: true });
        data = res.data;
      } else {
        const deviceId = localStorage.getItem('device_id') || '';
        const res = await axios.post(`${API}/auth/login`, { email, password, remember_me: true, device_id: deviceId });
        data = res.data;
      }
      saveTokens(data.access_token, data.refresh_token);
      if (!data.refresh_token) localStorage.removeItem("refresh_token");
      // Save new account
      const accounts = getSavedAccounts();
      const existing = accounts.findIndex(a => a.email === data.email);
      const entry = { email: data.email, name: data.name || switchName, token: data.access_token, refresh_token: data.refresh_token };
      if (existing >= 0) accounts[existing] = entry;
      else accounts.push(entry);
      localStorage.setItem('saved_accounts', JSON.stringify(accounts));
      try { localStorage.setItem("cached_user", JSON.stringify({ id: data.id, email: data.email, name: data.name, role: data.role })); } catch {}
      SWITCH_CLEAR_KEYS.forEach(k => localStorage.removeItem(k));
      onUserUpdate({ id: data.id, email: data.email, name: data.name, role: data.role });
      setOpen(false);
      toast.success(`Switched to ${data.email}`);
      window.location.reload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error("Enter your password"); return; }
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, { data: { password: deletePassword, device_id: myDeviceId } });
      setShowDeleteConfirm(false);
      setOpen(false);
      toast.success("Account deleted");
      handleLogout();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
    setDeleting(false);
  };

  const MenuItem = ({ icon: Icon, label, value, onClick, testId }) => (
    <button onClick={onClick} className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0" data-testid={testId}>
      <div className="flex items-center gap-3">
        <Icon className="w-4 h-4 text-zinc-400" />
        <div className="text-left">
          <p className="text-sm font-medium text-zinc-800">{label}</p>
          {value && <p className="text-xs text-zinc-400">{value}</p>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-300" />
    </button>
  );

  const SECTION_PARENT = { name: "account", email: "account", password: "account", subscription: "account", "cut-history": "cutlist-hub", "default-page": "app-settings", "viewable-only": "app-settings", "auto-deduct": "app-settings", "highlight-bar": "app-settings", "cut-lists": "app-settings", "notifications": "app-settings", "order-alerts": "app-settings", "owner": "account", "admin-backup": "admin", "admin-financials": "admin", "admin-customers": "admin" };
  const BackButton = () => (
    <button onClick={() => { if (editing) setEditing(false); else setSection(SECTION_PARENT[section] || null); }} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 mb-4" data-testid="back-btn">
      <ChevronRight className="w-4 h-4 rotate-180" />
      <span>{editing ? "Back" : "Back"}</span>
    </button>
  );

  return (
    <>
      <button onClick={handleOpen} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors rounded-sm" data-testid="account-btn" title="Account">
        <Menu className="w-4 h-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-none max-w-sm p-0 max-h-[85vh] overflow-y-auto [&>button]:hidden" data-testid="account-dialog">
          <div className="p-5 pb-3 border-b border-zinc-100 min-w-0">
            <DialogHeader>
              <div className="flex items-start justify-between gap-3">
              <DialogTitle className="text-lg font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                {section === null ? "Settings" : section === "account" ? "Account" : section === "cutlist-hub" ? "Cut List" : section === "app-settings" ? "App Settings" : section === "notifications" ? "Notifications Permission" : section === "order-alerts" ? "Order Alerts" : section === "name" ? (editing ? "Change Name" : "Name") : section === "email" ? (editing ? "Change Email" : "Email") : section === "password" ? (editing ? "Change Password" : "Password") : section === "prints" ? "Print History" : section === "default-page" ? "Default Page" : section === "owner" ? "Owner" : section === "auto-deduct" ? "Lumber Auto-Deduction" : section === "highlight-bar" ? "Highlight Bar" : section === "calendar-setting" ? "Calendar" : section === "cut-lists" ? "Cut List Display" : section === "subscription" ? "Subscription" : section === "admin" ? "Admin Panel" : section === "admin-backup" ? "Backup" : section === "admin-financials" ? "Financials" : section === "admin-customers" ? "Customers" : section === "cut-history" ? "Total Cut List Components History" : section === "whats-new" ? "What's New" : "Switch Account"}
              </DialogTitle>
              <button onClick={() => setOpen(false)} className="p-1.5 -mt-1 -mr-1 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors flex-shrink-0" data-testid="settings-close-x">
                <X className="w-6 h-6" />
              </button>
              </div>
            </DialogHeader>
            {section === null && <p className="text-xs text-zinc-400 mt-1">{user.email}</p>}
          </div>

          <div className="p-5 pt-3 min-w-0">
            {/* Main Menu */}
            {section === null && (
              <div>
                {isOwnerDevice ? (
                  <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-orange-600 text-white" data-testid="owner-ribbon">
                    <Shield className="w-4 h-4 flex-shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-[0.25em]">Owner</span>
                    <span className="text-[10px] text-orange-100 ml-auto text-right">This device controls the account</span>
                  </div>
                ) : (
                  <div className="mb-3 px-3 py-2 border border-zinc-200 bg-zinc-50" data-testid="owner-info-strip">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Owner</p>
                    <p className="text-xs text-zinc-600">{ownerLabelUA ? deviceLabel({ user_agent: ownerLabelUA, model: ownerModel }) : "Another device"} controls this account</p>
                  </div>
                )}
                <div className="border border-zinc-200 divide-y divide-zinc-100 mb-4">
                  <MenuItem icon={Pen} label="Account" value="Name, email, subscription & password" onClick={() => goToSection("account")} testId="menu-account" />
                  <MenuItem icon={History} label="Cut List" value="Total monthly & components history" onClick={() => goToSection("cutlist-hub")} testId="menu-cutlist-hub" />
                  <MenuItem icon={Monitor} label="App Settings" value="Display & behavior options" onClick={() => goToSection("app-settings")} testId="menu-app-settings" />
                  {user?.is_admin && (
                    <MenuItem icon={Shield} label="Admin Panel" value="Backup, financials & customers" onClick={() => goToSection("admin")} testId="menu-admin-panel" />
                  )}
                  <MenuItem icon={HelpCircle} label="App Tutorial" value="Learn how to use the app" onClick={() => { setOpen(false); onStartTutorial && onStartTutorial(); }} testId="menu-tutorial" />
                  <MenuItem icon={Shield} label="Privacy Policy" value="How we handle your data" onClick={() => { setOpen(false); onShowPrivacy && onShowPrivacy(); }} testId="menu-privacy" />
                  <MenuItem icon={Shield} label="Terms of Service" value="Rules for using the app" onClick={() => { setOpen(false); onShowTerms && onShowTerms(); }} testId="menu-terms" />
                </div>
                <Button onClick={() => setShowLogoutConfirm(true)} variant="outline"
                  className="w-full rounded-none border-red-300 text-red-600 hover:bg-red-50 mb-2" data-testid="account-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />Sign Out
                </Button>
                <Button onClick={() => { if (!isOwnerDevice) { toast.error("Only the Owner device can delete this account"); return; } setShowDeleteConfirm(true); }} variant="outline"
                  className={`w-full rounded-none mb-2 ${isOwnerDevice ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-zinc-200 text-zinc-400 hover:bg-zinc-50'}`} data-testid="delete-account-btn">
                  <Trash2 className="w-4 h-4 mr-2" />Delete Account{!isOwnerDevice && <span className="ml-1 text-[10px]">(Owner device only)</span>}
                </Button>
                <Button onClick={() => setOpen(false)} variant="outline"
                  className="w-full rounded-none border-zinc-300 text-zinc-600 hover:bg-zinc-50" data-testid="settings-return-btn">
                  <ChevronRight className="w-4 h-4 mr-2 rotate-180" />Return
                </Button>
              </div>
            )}

            {/* Account hub */}
            {section === "account" && (
              <div className="space-y-4">
                <BackButton />
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  <MenuItem icon={Pen} label="Name" value={user.name || "Not set"} onClick={() => goToSection("name")} testId="menu-name" />
                  <MenuItem icon={Mail} label="Email" value={user.email} onClick={() => goToSection("email")} testId="menu-email" />
                  <MenuItem icon={CreditCard} label="Subscription" value={isOwnerDevice ? "Plan, billing & payment method" : "Locked — Owner device only"} onClick={async () => { goToSection("subscription"); setSubCancelConfirm(false); if (localStorage.getItem('is_owner_device') !== '1') return; setMySubHistory(null); try { const { data } = await axios.get(`${API}/billing/status`); setMySub(data); } catch { setMySub({ access: "offline" }); } try { const { data } = await axios.get(`${API}/billing/history`); setMySubHistory(data); } catch { setMySubHistory({ total_paid: 0, total_refunded: 0, transactions: [], payment_method: null, error: true }); } }} testId="menu-subscription" />
                  <MenuItem icon={KeyRound} label="Password" value="********" onClick={() => goToSection("password")} testId="menu-password" />
                  <MenuItem icon={Shield} label="Owner" value={isOwnerDevice ? "Devices & view-only page control" : "Locked — Owner device only"} onClick={() => { goToSection("owner"); setOwnerSelected(null); if (localStorage.getItem('is_owner_device') === '1') loadOwnerDevices(); }} testId="menu-owner" />
                </div>
              </div>
            )}

            {/* Cut List hub */}
            {section === "cutlist-hub" && (
              <div className="space-y-4">
                <BackButton />
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  <MenuItem icon={History} label="Total Monthly Cut History" value="All cut lists combined, grouped by month" onClick={() => { setOpen(false); onShowMonthlyHistory && onShowMonthlyHistory(); }} testId="menu-monthly-history" />
                  <MenuItem icon={History} label="Total Cut List Components History" value="Completed components from all cut lists combined" onClick={() => { goToSection("cut-history"); fetchCutHistory(); }} testId="menu-cut-history" />
                </div>
              </div>
            )}

            {/* App Settings hub */}
            {section === "app-settings" && (
              <div className="space-y-4">
                <BackButton />
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  <MenuItem icon={Monitor} label="Default Page" value={(() => { const stored = localStorage.getItem('default_page'); const migrate = { shop: 'calculator', cutting: 'cutlist', office: 'calculator' }; const p = migrate[stored] || stored || 'calculator'; const labels = { calculator: 'Calculator', cutlist: 'Cut List', lumber: 'Lumber', orders: 'Orders' }; return labels[p] || 'Calculator'; })()} onClick={() => goToSection("default-page")} testId="menu-default-page" />
                  <MenuItem icon={Layers} label="Lumber Auto-Deduction" value={localStorage.getItem('lumber_auto_deduct') === 'off' ? 'Off' : 'On'} onClick={() => goToSection("auto-deduct")} testId="menu-auto-deduct" />
                  <MenuItem icon={Highlighter} label="Highlight Bar" value={localStorage.getItem('highlight_bar') === 'on' ? 'On' : 'Off'} onClick={() => goToSection("highlight-bar")} testId="menu-highlight-bar" />
                  <MenuItem icon={CalendarDays} label="Calendar" value={localStorage.getItem('calendar_date_field') === 'deliver' ? 'Marks delivery dates' : 'Marks load dates'} onClick={() => goToSection("calendar-setting")} testId="menu-calendar-setting" />
                  <MenuItem icon={ClipboardList} label="Cut List Display" value="Manage lists & display" onClick={() => { goToSection("cut-lists"); setClDisplay(localStorage.getItem('active_cut_list') || 'all'); loadCutLists(); }} testId="menu-cut-lists" />
                  <MenuItem icon={Bell} label="Notifications Permission" value={localStorage.getItem('notifications_enabled') === 'on' ? 'On' : 'Off'} onClick={() => goToSection("notifications")} testId="menu-notifications" />
                  <MenuItem icon={Bell} label="Order Alerts" value={localStorage.getItem('order_alerts') === 'on' ? 'On' : 'Off'} onClick={() => goToSection("order-alerts")} testId="menu-order-alerts" />
                </div>
              </div>
            )}

            {/* Name - View */}
            {section === "name" && !editing && (
              <div className="space-y-4">
                <BackButton />
                <div className="p-4 bg-zinc-50 border border-zinc-200">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Name</p>
                  <p className="text-base font-semibold text-zinc-800">{user.name || "Not set"}</p>
                </div>
                <Button onClick={() => { setName(user.name || ""); setEditing(true); }} variant="outline"
                  className="w-full rounded-none border-zinc-300" data-testid="edit-name-btn">
                  <Pencil className="w-4 h-4 mr-2" />Change Name
                </Button>
              </div>
            )}
            {/* Name - Edit */}
            {section === "name" && editing && (
              <div className="space-y-4">
                <BackButton />
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus className="rounded-none border-zinc-300" data-testid="account-name-input" />
                </div>
                <Button onClick={saveName} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-none font-semibold" data-testid="save-name-btn">
                  <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save"}
                </Button>
              </div>
            )}

            {/* Email - View */}
            {section === "email" && !editing && (
              <div className="space-y-4">
                <BackButton />
                <div className="p-4 bg-zinc-50 border border-zinc-200">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Email</p>
                  <p className="text-base font-semibold text-zinc-800">{user.email}</p>
                </div>
                <Button onClick={() => { setEmail(user.email || ""); setEmailPassword(""); setEditing(true); }} variant="outline"
                  className="w-full rounded-none border-zinc-300" data-testid="edit-email-btn">
                  <Pencil className="w-4 h-4 mr-2" />Change Email
                </Button>
              </div>
            )}
            {/* Email - Edit */}
            {section === "email" && editing && (
              <div className="space-y-4">
                <BackButton />
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">New Email</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} autoFocus className="rounded-none border-zinc-300" data-testid="account-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">Confirm Password</Label>
                  <Input type="password" value={emailPassword} onChange={(e) => setEmailPassword(e.target.value)} placeholder="Enter your password" className="rounded-none border-zinc-300" data-testid="email-confirm-password-input" />
                </div>
                <Button onClick={saveEmail} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-none font-semibold" data-testid="save-email-btn">
                  <Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Update Email"}
                </Button>
              </div>
            )}

            {/* Password - View */}
            {section === "password" && !editing && (
              <div className="space-y-4">
                <BackButton />
                <div className="p-4 bg-zinc-50 border border-zinc-200">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Password</p>
                  <p className="text-base font-semibold text-zinc-800">********</p>
                </div>
                <Button onClick={() => { setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setEditing(true); }} variant="outline"
                  className="w-full rounded-none border-zinc-300" data-testid="edit-password-btn">
                  <Pencil className="w-4 h-4 mr-2" />Change Password
                </Button>
              </div>
            )}
            {/* Password - Edit */}
            {section === "password" && editing && (
              <div className="space-y-4">
                <BackButton />
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">Current Password</Label>
                  <div className="relative">
                    <Input type={showCurrent ? "text" : "password"} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoFocus placeholder="Enter current password" className="rounded-none border-zinc-300 pr-10" data-testid="current-password-input" />
                    <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                      {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">New Password</Label>
                  <div className="relative">
                    <Input type={showNew ? "text" : "password"} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" className="rounded-none border-zinc-300 pr-10" data-testid="new-password-input" />
                    <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600">
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">Confirm New Password</Label>
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repeat new password" className="rounded-none border-zinc-300" data-testid="confirm-password-input" />
                  {confirmPassword && newPassword !== confirmPassword && <p className="text-xs text-red-500 mt-1">Passwords don't match</p>}
                </div>
                <Button onClick={changePassword} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-none font-semibold" data-testid="change-password-btn">
                  <KeyRound className="w-4 h-4 mr-2" />{saving ? "Changing..." : "Change Password"}
                </Button>
              </div>
            )}

            {/* Switch Account */}
            {section === "switch" && (
              <div className="space-y-4">
                <BackButton />
                {/* Saved accounts list */}
                {(() => {
                  const accounts = getSavedAccounts().filter(a => a.email !== user.email);
                  return accounts.length > 0 ? (
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {accounts.map(a => (
                        <button key={a.email} onClick={() => switchToAccount(a)}
                          className="w-full flex items-center justify-between p-3 hover:bg-zinc-50 transition-colors" data-testid={`switch-to-${a.email}`}>
                          <div className="text-left">
                            <p className="text-sm font-medium text-zinc-800">{a.name || a.email}</p>
                            <p className="text-xs text-zinc-400">{a.email}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-300" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-400 text-center py-2">No other accounts saved on this device</p>
                  );
                })()}

                {/* Add account */}
                {!showAddAccount ? (
                  <button onClick={() => { setShowAddAccount(true); setSwitchMode("login"); setSwitchEmail(""); setSwitchPassword(""); setSwitchName(""); }}
                    className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-zinc-300 text-zinc-500 hover:border-orange-400 hover:text-orange-600 transition-colors"
                    data-testid="add-account-btn">
                    <UserPlus className="w-4 h-4" />
                    <span className="text-sm font-medium">Add Account</span>
                  </button>
                ) : (
                  <div className="border border-zinc-200 p-3 space-y-3">
                    <div className="flex border-b border-zinc-200 mb-2">
                      <button onClick={() => setSwitchMode("login")}
                        className={`flex-1 py-1.5 text-xs font-semibold text-center ${switchMode === "login" ? "border-b-2 border-orange-600 text-orange-600" : "text-zinc-400"}`}>
                        Log In
                      </button>
                      <button onClick={() => setSwitchMode("register")}
                        className={`flex-1 py-1.5 text-xs font-semibold text-center ${switchMode === "register" ? "border-b-2 border-orange-600 text-orange-600" : "text-zinc-400"}`}>
                        Create Account
                      </button>
                    </div>
                    {switchMode === "register" && (
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">Name</Label>
                        <Input value={switchName} onChange={(e) => setSwitchName(e.target.value)} placeholder="Your name" className="rounded-none border-zinc-300" data-testid="switch-name-input" />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">Email</Label>
                      <Input type="email" inputMode="email" autoCapitalize="none" autoCorrect="off" autoComplete="email" value={switchEmail} onChange={(e) => setSwitchEmail(e.target.value)} autoFocus placeholder="Enter email" className="rounded-none border-zinc-300" data-testid="switch-email-input" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 mb-1 block">Password</Label>
                      <Input type="password" value={switchPassword} onChange={(e) => setSwitchPassword(e.target.value)} placeholder="Enter password" className="rounded-none border-zinc-300" data-testid="switch-password-input" />
                    </div>
                    <Button onClick={switchAccount} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-700 text-white rounded-none font-semibold" data-testid="switch-account-btn">
                      <UserPlus className="w-4 h-4 mr-2" />{saving ? "..." : switchMode === "register" ? "Create & Switch" : "Log In & Switch"}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Default Page */}
            {section === "default-page" && (
              <div className="space-y-4">
                <BackButton />
                <p className="text-sm text-zinc-500">Choose which page opens when you start the app on this device.</p>
                {[["calculator", "Calculator"], ["cutlist", "Cut List"], ["lumber", "Lumber"], ["orders", "Orders"]].map(([page, label]) => {
                  const stored = localStorage.getItem('default_page');
                  const migrate = { shop: 'calculator', cutting: 'cutlist', office: 'calculator' };
                  const current = migrate[stored] || stored || 'calculator';
                  const match = current === page;
                  return (
                    <button key={page} onClick={() => { localStorage.setItem('default_page', page); setSection(null); toast.success(`Default page set to ${label}`); }}
                      className={`w-full flex items-center justify-between p-3 border transition-colors ${match ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid={`default-page-${page}`}>
                      <span className="text-sm font-medium text-zinc-800">{label}</span>
                      {match && <span className="text-xs font-bold text-orange-600">Current</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Admin Panel menu */}
            {section === "admin" && (
              <div className="space-y-4">
                <BackButton />
                <div className="border border-zinc-200 divide-y divide-zinc-100">
                  <MenuItem icon={Save} label="Backup" value="Email or download a database backup" onClick={() => { goToSection("admin-backup"); axios.get(`${API}/admin/backup/status`).then(({ data }) => setBackupStatus(data.last_backup_email)).catch(() => {}); }} testId="admin-menu-backup" />
                  <MenuItem icon={CreditCard} label="Financials" value="Revenue, refunds & upcoming transactions" onClick={() => { goToSection("admin-financials"); loadFinancials(); }} testId="admin-menu-financials" />
                  <MenuItem icon={User} label="Customers" value="Accounts, devices, visits & email everyone" onClick={() => { goToSection("admin-customers"); setShowEmailCompose(false); loadAdminAccounts(); loadVisits(); }} testId="admin-menu-customers" />
                </div>
              </div>
            )}

            {/* Admin: Backup */}
            {section === "admin-backup" && (
              <div className="space-y-3">
                <BackButton />
                <div className="p-3 border border-zinc-200 bg-zinc-50 space-y-2" data-testid="admin-backup-card">
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Database Backup</p>
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      try {
                        toast.info("Preparing backup…");
                        const { data } = await axios.get(`${API}/admin/backup`);
                        const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = `pallet-backup-${new Date().toISOString().slice(0, 10)}.json`;
                        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                        toast.success("Backup downloaded — check your Downloads folder");
                      } catch { toast.error("Backup failed"); }
                    }}
                      className="flex-1 py-2 text-xs font-bold bg-zinc-800 text-white hover:bg-zinc-700" data-testid="admin-download-backup">
                      Download Backup
                    </button>
                    <button onClick={async () => {
                      try {
                        toast.info("Sending backup email…");
                        await axios.post(`${API}/admin/backup/send-email`);
                        setBackupStatus(new Date().toISOString());
                        toast.success("Backup emailed to you");
                      } catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
                    }}
                      className="flex-1 py-2 text-xs font-bold border border-zinc-300 text-zinc-700 hover:bg-zinc-100" data-testid="admin-email-backup">
                      Email Me Now
                    </button>
                  </div>
                  <p className="text-[11px] text-zinc-400" data-testid="admin-backup-status">
                    Automatic weekly backup email is on.{backupStatus ? ` Last sent ${new Date(backupStatus).toLocaleDateString()}.` : " No backup sent yet — one goes out shortly."}
                  </p>
                </div>
              </div>
            )}

            {/* Admin: Financials */}
            {section === "admin-financials" && (
              <div className="space-y-3">
                <BackButton />
                {adminFinancials === null ? (
                  <p className="text-sm text-zinc-400">Loading financials…</p>
                ) : adminFinancials.error ? (
                  <p className="text-sm text-red-600" data-testid="admin-financials-error">{adminFinancials.error}</p>
                ) : (
                  <>
                    <div className="border border-zinc-200 divide-y divide-zinc-100" data-testid="admin-financials-card">
                      <div className="p-3 flex items-center justify-between gap-2">
                        <span className="text-xs uppercase tracking-wider text-zinc-400">Year-To-Date Revenue</span>
                        <span className="text-sm font-bold text-zinc-900" data-testid="admin-fin-ytd">${adminFinancials.ytd_revenue.toFixed(2)}</span>
                      </div>
                      <div className="p-3 flex items-center justify-between gap-2">
                        <span className="text-xs uppercase tracking-wider text-zinc-400">Year-To-Date Refunded</span>
                        <span className={`text-sm font-bold ${(adminFinancials.ytd_refunded || 0) > 0 ? "text-red-600" : "text-zinc-900"}`} data-testid="admin-fin-ytd-refunded">${(adminFinancials.ytd_refunded || 0).toFixed(2)}</span>
                      </div>
                      <div className="p-3 flex items-center justify-between gap-2">
                        <span className="text-xs uppercase tracking-wider text-zinc-400">Total Revenue</span>
                        <span className="text-sm font-bold text-zinc-900" data-testid="admin-fin-total">${adminFinancials.total_revenue.toFixed(2)}</span>
                      </div>
                      <div className="p-3 flex items-center justify-between gap-2">
                        <span className="text-xs uppercase tracking-wider text-zinc-400">Total Refunded</span>
                        <span className={`text-sm font-bold ${adminFinancials.total_refunded > 0 ? "text-red-600" : "text-zinc-900"}`} data-testid="admin-fin-refunded">${adminFinancials.total_refunded.toFixed(2)}</span>
                      </div>
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Next 5 Scheduled Transactions</p>
                    <div className="border border-zinc-200 divide-y divide-zinc-100" data-testid="admin-fin-upcoming">
                      {adminFinancials.upcoming.length === 0 && (
                        <p className="p-3 text-xs text-zinc-400">No upcoming customer transactions scheduled.</p>
                      )}
                      {adminFinancials.upcoming.map((u, i) => (
                        <div key={i} className="p-3 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-zinc-800 break-all">{u.email || "Unknown customer"}</p>
                            <p className="text-[10px] text-zinc-400">{u.date ? new Date(u.date + "T00:00:00").toLocaleDateString() : "—"}</p>
                          </div>
                          <span className="text-xs font-bold text-zinc-900 flex-shrink-0">${u.amount.toFixed(2)}{u.interval ? `/${u.interval === "year" ? "yr" : "mo"}` : ""}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Admin: Customers */}
            {section === "admin-customers" && (
              <div className="space-y-3">
                {adminSelected ? (
                  <>
                    <button onClick={() => { setAdminSelected(null); setAdminDevices(null); }} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 mb-1" data-testid="admin-back-to-accounts">
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      <span>All Accounts</span>
                    </button>
                    <div className={`p-3 border ${adminSelected.is_frozen ? "border-blue-300 bg-blue-50" : "border-zinc-200 bg-zinc-50"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-bold text-zinc-800 break-all">{adminSelected.email}</p>
                        {adminSelected.is_frozen && <span className="text-[10px] font-bold tracking-wider text-blue-700 bg-blue-100 border border-blue-300 px-1.5 py-0.5 flex-shrink-0" data-testid="admin-frozen-badge">FROZEN</span>}
                      </div>
                      {adminSelected.name && <p className="text-xs text-zinc-500">{adminSelected.name}</p>}
                    </div>
                    {!adminSelected.is_panel_admin && (
                      <div className="p-3 border border-zinc-200 space-y-2" data-testid="admin-freeze-card">
                        {!adminSelected.is_frozen && (
                          <Input value={freezeReason} onChange={(e) => setFreezeReason(e.target.value)} maxLength={200}
                            placeholder="Reason (optional — shown to the user)"
                            className="h-8 text-sm rounded-none border-zinc-300" data-testid="admin-freeze-reason-input" />
                        )}
                        {adminSelected.is_frozen && adminSelected.frozen_reason && (
                          <p className="text-xs text-zinc-600" data-testid="admin-frozen-reason"><span className="font-bold">Reason:</span> {adminSelected.frozen_reason}</p>
                        )}
                        <button onClick={toggleFreeze} disabled={adminBillingSaving}
                          className={`w-full py-2 text-xs font-bold border transition-colors ${adminSelected.is_frozen ? "border-green-400 text-green-700 hover:bg-green-50" : "border-blue-400 text-blue-700 hover:bg-blue-50"} disabled:opacity-50`}
                          data-testid="admin-toggle-freeze">
                          {adminSelected.is_frozen ? "Unfreeze Account" : "Freeze Account"}
                        </button>
                        <p className="text-[11px] text-zinc-400">{adminSelected.is_frozen ? "This user is locked out — they can't log in or use the app until you unfreeze." : "Freezing instantly logs the user out on all devices and blocks them from logging back in."}</p>
                      </div>
                    )}
                    {/* Billing / Subscription info */}
                    {adminBilling === null ? (
                      <p className="text-sm text-zinc-400">Loading subscription…</p>
                    ) : (
                      <div className="border border-zinc-200 divide-y divide-zinc-100" data-testid="admin-billing-card">
                        <div className="p-3 flex items-center justify-between gap-2">
                          <span className="text-xs uppercase tracking-wider text-zinc-400">Status</span>
                          <span className={`text-xs font-bold text-right ${adminBilling.access === "locked" ? "text-red-600" : adminBilling.access === "trial" ? "text-amber-600" : "text-green-700"}`} data-testid="admin-billing-status">{accessLabel(adminBilling)}</span>
                        </div>
                        <div className="p-3 flex items-center justify-between gap-2">
                          <span className="text-xs uppercase tracking-wider text-zinc-400">Created</span>
                          <span className="text-xs font-semibold text-zinc-800" data-testid="admin-billing-created">{adminBilling.created_at ? new Date(adminBilling.created_at).toLocaleDateString() : "—"}</span>
                        </div>
                        <div className="p-3 flex items-center justify-between gap-2">
                          <span className="text-xs uppercase tracking-wider text-zinc-400">Total Paid</span>
                          <span className="text-xs font-bold text-zinc-900" data-testid="admin-billing-total-paid">${adminBilling.total_paid.toFixed(2)}</span>
                        </div>
                        <div className="p-3 flex items-center justify-between gap-2">
                          <span className="text-xs uppercase tracking-wider text-zinc-400">Total Refunded</span>
                          <span className={`text-xs font-bold ${(adminBilling.total_refunded || 0) > 0 ? "text-red-600" : "text-zinc-900"}`} data-testid="admin-billing-total-refunded">${(adminBilling.total_refunded || 0).toFixed(2)}</span>
                        </div>
                        {adminBilling.access === "subscribed" && adminBilling.current_period_end && (
                          <div className="p-3 flex items-center justify-between gap-2">
                            <span className="text-xs uppercase tracking-wider text-zinc-400">Renews</span>
                            <span className="text-xs font-semibold text-zinc-800">{new Date(adminBilling.current_period_end).toLocaleDateString()}{adminBilling.cancel_at_period_end ? " (canceling)" : ""}</span>
                          </div>
                        )}
                        {!adminBilling.is_panel_admin && (
                          <>
                            <div className="p-3 flex items-center justify-between gap-2">
                              <span className="text-xs uppercase tracking-wider text-zinc-400">Discount %</span>
                              <div className="flex items-center gap-1.5">
                                <Input type="number" min="0" max="100" value={adminDiscount} onChange={(e) => setAdminDiscount(e.target.value)}
                                  className="w-16 h-8 text-sm text-center rounded-none border-zinc-300" data-testid="admin-discount-input" />
                                <button onClick={() => saveAdminBilling({ discount_percent: parseInt(adminDiscount) || 0 })} disabled={adminBillingSaving}
                                  className="h-8 px-2.5 text-xs font-bold bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50" data-testid="admin-save-discount">
                                  Save
                                </button>
                              </div>
                            </div>
                            <div className="p-3">
                              <button onClick={() => saveAdminBilling({ access_override: adminBilling.access_override === "free" ? null : "free" })} disabled={adminBillingSaving}
                                className={`w-full py-2 text-xs font-bold border transition-colors ${adminBilling.access_override === "free" ? "border-red-300 text-red-600 hover:bg-red-50" : "border-green-400 text-green-700 hover:bg-green-50"} disabled:opacity-50`}
                                data-testid="admin-toggle-free-access">
                                {adminBilling.access_override === "free" ? "Remove Free Access" : "Give Free Access (no subscription needed)"}
                              </button>
                            </div>
                          </>
                        )}
                        {adminBilling.is_panel_admin && (
                          <div className="p-3"><p className="text-[11px] text-zinc-400">Admin account — always free, no subscription needed.</p></div>
                        )}
                      </div>
                    )}
                    {adminDevices === null ? (
                      <p className="text-sm text-zinc-400">Loading devices…</p>
                    ) : (
                      <>
                        <p className="text-sm text-zinc-700" data-testid="admin-device-count">
                          <span className="text-2xl font-bold text-orange-600">{adminDevices.count}</span> device{adminDevices.count === 1 ? '' : 's'} on this account
                        </p>
                        <div className="space-y-2">
                          {adminDevices.devices.map((d, i) => (
                            <div key={i} className="p-2 border border-zinc-200" data-testid={`admin-device-row-${i}`}>
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-xs font-semibold text-zinc-800">{deviceLabel(d)}</p>
                                <p className="text-[10px] text-zinc-400 flex-shrink-0">Last seen: {d.last_seen ? new Date(d.last_seen).toLocaleString() : '—'}</p>
                              </div>
                              {deviceDetail(d) && <p className="text-[10px] text-zinc-500 mt-0.5">{deviceDetail(d)}</p>}
                            </div>
                          ))}
                          {adminDevices.devices.length === 0 && <p className="text-xs text-zinc-400">No devices recorded yet. Devices are counted after this update as people use the app.</p>}
                        </div>
                      </>
                    )}
                  </>
                ) : showEmailCompose ? (
                  <>
                    <button onClick={() => setShowEmailCompose(false)} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 mb-1" data-testid="email-compose-back">
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      <span>Back</span>
                    </button>
                    <p className="text-sm font-bold text-zinc-800">Email Customers</p>
                    <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} maxLength={150}
                      placeholder="Subject" className="rounded-none border-zinc-300 text-sm" data-testid="email-customers-subject" />
                    <textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} maxLength={5000} rows={5}
                      placeholder="Type your message to customers…"
                      className="w-full border border-zinc-300 p-2 text-sm rounded-none resize-none focus:outline-none focus:ring-2 focus:ring-orange-600"
                      data-testid="email-customers-message" />
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Send To ({emailTargets.length} selected)</p>
                    <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-44 overflow-y-auto" data-testid="email-customers-list">
                      {adminAccounts.filter(a => !a.deleted_at).map(a => {
                        const on = emailTargets.includes(a.email);
                        return (
                          <button key={a.id} onClick={() => setEmailTargets(t => on ? t.filter(e => e !== a.email) : [...t, a.email])}
                            className="w-full flex items-center gap-2 p-2.5 text-left hover:bg-zinc-50" data-testid={`email-target-${a.email}`}>
                            <span className={`w-4 h-4 flex-shrink-0 border flex items-center justify-center ${on ? 'bg-orange-600 border-orange-600' : 'border-zinc-300'}`}>
                              {on && <Check className="w-3 h-3 text-white" />}
                            </span>
                            <span className="text-xs text-zinc-700 break-all">{a.email}</span>
                          </button>
                        );
                      })}
                    </div>
                    <button onClick={sendCustomerEmail} disabled={emailSending || !emailSubject.trim() || !emailMessage.trim() || emailTargets.length === 0}
                      className="w-full py-2.5 text-sm font-bold bg-zinc-800 text-white hover:bg-zinc-900 disabled:opacity-50" data-testid="email-customers-send">
                      {emailSending ? "Sending…" : `Send to ${emailTargets.length} customer${emailTargets.length === 1 ? '' : 's'}`}
                    </button>
                  </>
                ) : (
                  <>
                    <BackButton />
                    <div className="grid grid-cols-2 gap-2" data-testid="admin-customer-stats">
                      <div className="p-3 border border-zinc-200 bg-zinc-50 text-center">
                        <p className="text-2xl font-bold text-orange-600" data-testid="admin-active-customers">{adminAccounts.filter(a => !a.deleted_at).length}</p>
                        <p className="text-[10px] uppercase tracking-wider text-zinc-400">Active Customers</p>
                      </div>
                      <div className="p-3 border border-zinc-200 bg-zinc-50 text-center">
                        <p className="text-2xl font-bold text-orange-600" data-testid="admin-total-devices">{adminAccounts.reduce((s, a) => s + (a.device_count || 0), 0)}</p>
                        <p className="text-[10px] uppercase tracking-wider text-zinc-400">Devices</p>
                      </div>
                    </div>
                    <div className="p-3 border border-zinc-200 bg-zinc-50 space-y-2" data-testid="admin-visits-card">
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Site Visits</p>
                      {adminVisits === null ? (
                        <p className="text-xs text-zinc-400">Loading visits…</p>
                      ) : (
                        <>
                          <div className="flex gap-6">
                            <div>
                              <p className="text-xl font-bold text-zinc-900" data-testid="admin-total-visits">{adminVisits.total_visits}</p>
                              <p className="text-[10px] uppercase tracking-wider text-zinc-400">Total Visits</p>
                            </div>
                            <div>
                              <p className="text-xl font-bold text-zinc-900" data-testid="admin-unique-visitors">{adminVisits.unique_visitors}</p>
                              <p className="text-[10px] uppercase tracking-wider text-zinc-400">Unique Visitors</p>
                            </div>
                          </div>
                          {adminVisits.last_30_days.length > 0 && (
                            <div className="max-h-32 overflow-y-auto border border-zinc-200 divide-y divide-zinc-100 bg-white" data-testid="admin-visits-daily">
                              {adminVisits.last_30_days.map((d) => (
                                <div key={d.date} className="px-2 py-1.5 flex items-center justify-between text-[11px]">
                                  <span className="text-zinc-500">{new Date(d.date + "T00:00:00").toLocaleDateString()}</span>
                                  <span className="text-zinc-800 font-semibold">{d.visits} visit{d.visits === 1 ? '' : 's'} · {d.unique} unique</span>
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="text-[10px] text-zinc-400">Every page load counts — even visitors who never sign in. Counting started when this feature went live.</p>
                        </>
                      )}
                    </div>
                    <button onClick={() => { setEmailTargets(adminAccounts.filter(a => !a.deleted_at).map(a => a.email)); setShowEmailCompose(true); }}
                      className="w-full py-2.5 text-sm font-bold border border-zinc-300 text-zinc-700 hover:bg-zinc-50 flex items-center justify-center gap-2" data-testid="admin-email-all-btn">
                      <Mail className="w-4 h-4" /> Email All Customers
                    </button>
                    <p className="text-sm text-zinc-500">{adminLoading ? "Loading accounts…" : `${adminAccounts.length} account${adminAccounts.length === 1 ? '' : 's'} — tap one to see its devices.`}</p>
                    {adminAccounts.map(a => (
                      <button key={a.id} onClick={() => loadAdminDevices(a)}
                        className="w-full flex items-center justify-between gap-2 p-3 border border-zinc-200 hover:bg-zinc-50 text-left"
                        data-testid={`admin-account-${a.email}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-zinc-800 break-all">{a.email}{a.is_frozen && <span className="ml-1.5 text-[10px] font-bold tracking-wider text-blue-700 bg-blue-100 border border-blue-300 px-1 py-0.5 align-middle" data-testid={`admin-frozen-label-${a.email}`}>FROZEN</span>}</p>
                          {a.name && <p className="text-xs text-zinc-400">{a.name}</p>}
                          {a.deleted_at && (
                            <p className="text-[10px] font-bold text-red-500" data-testid={`admin-deleted-label-${a.email}`}>
                              Deleted — purges {new Date(new Date(a.deleted_at).getTime() + 10 * 86400000).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <span className="flex items-center gap-1 text-xs font-bold text-zinc-500 flex-shrink-0">
                          {a.device_count} <Monitor className="w-3.5 h-3.5" />
                        </span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Lumber Auto-Deduction */}
            {section === "auto-deduct" && (
              <div className="space-y-4">
                <BackButton />
                <p className="text-sm text-zinc-500">When ON, any time the lumber amount gets adjusted in a component in the cut list, it changes that immediately in your Lumber Inventory. When OFF, adjusting lumber in a component does not touch the inventory. This setting applies to your whole account — every device signed in as you.</p>
                {[["on", "On — subtract from inventory (normal)"], ["off", "Off — don't touch inventory"]].map(([val, label]) => {
                  const current = localStorage.getItem('lumber_auto_deduct') === 'off' ? 'off' : 'on';
                  const match = current === val;
                  return (
                    <button key={val} onClick={async () => {
                      localStorage.setItem('lumber_auto_deduct', val);
                      setViewOnlyTick(t => t + 1);
                      try {
                        await axios.put(`${API}/auth/settings`, { auto_deduct: val === 'on' });
                        toast.success(`Lumber auto-deduction turned ${val} for your account`);
                      } catch {
                        toast.warning(`Couldn't reach the server — turned ${val} on this device only. Try again when you're back online.`);
                      }
                    }}
                      className={`w-full flex items-center justify-between p-3 border transition-colors ${match ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid={`auto-deduct-${val}`}>
                      <span className="text-sm font-medium text-zinc-800">{label}</span>
                      {match && <span className="text-xs font-bold text-orange-600">Current</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Calendar Setting */}
            {section === "calendar-setting" && (
              <div className="space-y-4">
                <BackButton />
                <p className="text-sm text-zinc-500">On the Orders page Calendar, each order's date gets a line (underline) marking it on the calendar. This setting picks WHICH date gets marked — the order's Load date or its Delivery date. Clicking a marked date shows the orders and events for that date. This changes the display on this device only.</p>
                {[["load", "Load date — mark the calendar on each order's load date"], ["deliver", "Delivery date — mark the calendar on each order's delivery date"]].map(([val, label]) => {
                  const current = localStorage.getItem('calendar_date_field') === 'deliver' ? 'deliver' : 'load';
                  const match = current === val;
                  return (
                    <button key={val} onClick={() => {
                      localStorage.setItem('calendar_date_field', val);
                      window.dispatchEvent(new Event('calendar-setting-changed'));
                      toast.success(`Calendar now marks ${val === 'deliver' ? 'delivery' : 'load'} dates`);
                      setSection("app-settings");
                    }}
                      className={`w-full flex items-center justify-between p-3 border transition-colors ${match ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid={`calendar-setting-${val}`}>
                      <span className="text-sm font-medium text-zinc-800 text-left">{label}</span>
                      {match && <span className="text-xs font-bold text-orange-600 ml-2 shrink-0">Current</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Highlight Bar */}
            {section === "highlight-bar" && (
              <div className="space-y-4">
                <BackButton />
                <p className="text-sm text-zinc-500">When ON, cut list items that are next to each other and share the same thickness get grouped inside a highlighted bar with a thickness label. When OFF, no highlighting is shown. This changes the display on this device only, and does not change anything across the rest of the account.</p>
                {[["on", "On — highlight matching thicknesses"], ["off", "Off — no highlight bar"]].map(([val, label]) => {
                  const current = localStorage.getItem('highlight_bar') === 'on' ? 'on' : 'off';
                  const match = current === val;
                  return (
                    <button key={val} onClick={() => {
                      localStorage.setItem('highlight_bar', val);
                      window.dispatchEvent(new Event('highlight-changed'));
                      setViewOnlyTick(t => t + 1);
                      toast.success(`Highlight bar turned ${val}`);
                    }}
                      className={`w-full flex items-center justify-between p-3 border transition-colors ${match ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid={`highlight-bar-${val}`}>
                      <span className="text-sm font-medium text-zinc-800">{label}</span>
                      {match && <span className="text-xs font-bold text-orange-600">Current</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Notifications */}
            {section === "notifications" && (
              <div className="space-y-4">
                <BackButton />
                <p className="text-sm text-zinc-500">When ON, this device is allowed to show notifications from the app (your device will ask you to allow them once). Turn on Order Alerts to get notified about orders. This setting is per device. On iPhone, the app must be added to your Home Screen (iOS 16.4 or newer) for notifications to work.</p>
                {[["on", "On — allow notifications on this device"], ["off", "Off — no notifications"]].map(([val, label]) => {
                  const current = localStorage.getItem('notifications_enabled') === 'on' ? 'on' : 'off';
                  const match = current === val;
                  return (
                    <button key={val} onClick={async () => {
                      if (current === val) return;
                      try {
                        if (val === 'on') { await enablePush(); toast.success("Notifications turned on"); }
                        else { await disablePush(); toast.success("Notifications turned off"); }
                      } catch (e) { toast.error(e.message || "Couldn't turn on notifications"); }
                      setViewOnlyTick(t => t + 1);
                    }}
                      className={`w-full flex items-center justify-between p-3 border transition-colors ${match ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid={`notifications-${val}`}>
                      <span className="text-sm font-medium text-zinc-800">{label}</span>
                      {match && <span className="text-xs font-bold text-orange-600">Current</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Order Alerts */}
            {section === "order-alerts" && (
              <div className="space-y-4">
                <BackButton />
                <p className="text-sm text-zinc-500">When ON, this device gets a notification when an order is created, or when an order's dates, pallet types or quantities change — from another device on this account. Notifications Permission must also be ON for this device. On iPhone, the app must be added to your Home Screen (iOS 16.4 or newer) for notifications to work.</p>
                {[["on", "On — alert this device about orders"], ["off", "Off — no order alerts"]].map(([val, label]) => {
                  const current = localStorage.getItem('order_alerts') === 'on' ? 'on' : 'off';
                  const match = current === val;
                  return (
                    <button key={val} onClick={async () => {
                      if (current === val) return;
                      await setOrderAlerts(val === 'on');
                      toast.success(`Order alerts turned ${val}`);
                      if (val === 'on' && localStorage.getItem('notifications_enabled') !== 'on') {
                        toast.info("Also turn Notifications ON so this device can receive them");
                      }
                      setViewOnlyTick(t => t + 1);
                    }}
                      className={`w-full flex items-center justify-between p-3 border transition-colors ${match ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                      data-testid={`order-alerts-${val}`}>
                      <span className="text-sm font-medium text-zinc-800">{label}</span>
                      {match && <span className="text-xs font-bold text-orange-600">Current</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Cut Lists management */}
            {section === "cut-lists" && (
              <div className="space-y-4">
                <BackButton />
                <p className="text-sm text-zinc-500">You can create multiple cut lists (for example one per saw or crew), pick which list new components go into, and choose below whether the Cut List page shows all lists together or just one at a time.</p>
                {clLists === null ? (
                  <p className="text-sm text-zinc-400">Loading…</p>
                ) : (
                  <>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-1.5">Your Cut Lists ({clLists.length}/15)</p>
                      <div className="space-y-1.5">
                        {clLists.map(list => (
                          <div key={list.id} className="flex items-center gap-2 p-2.5 border border-zinc-200" data-testid={`cutlist-row-${list.id}`}>
                            {clRenameId === list.id ? (
                              <>
                                <Input value={clRenameVal} onChange={(e) => setClRenameVal(e.target.value)} autoFocus maxLength={40}
                                  onKeyDown={async (e) => { if (e.key === 'Enter' && clRenameVal.trim()) { try { await axios.put(`${API}/cut-lists/${list.id}`, { name: clRenameVal.trim() }); await loadCutLists(); setClRenameId(null); toast.success("Renamed"); } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } } }}
                                  className="h-8 text-sm rounded-none border-zinc-300 flex-1" data-testid="cutlist-rename-input" />
                                <button onClick={async () => { if (!clRenameVal.trim()) return; try { await axios.put(`${API}/cut-lists/${list.id}`, { name: clRenameVal.trim() }); await loadCutLists(); setClRenameId(null); toast.success("Renamed"); } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } }}
                                  className="p-1.5 bg-zinc-800 text-white hover:bg-zinc-700" data-testid="cutlist-rename-save"><Check className="w-3.5 h-3.5" /></button>
                                <button onClick={() => setClRenameId(null)} className="p-1.5 text-zinc-400 hover:text-zinc-600"><X className="w-3.5 h-3.5" /></button>
                              </>
                            ) : (
                              <>
                                <span className="text-sm font-semibold text-zinc-800 flex-1 truncate">{list.name}</span>
                                <button onClick={() => { setClRenameId(list.id); setClRenameVal(list.name); }} className="p-1.5 text-zinc-400 hover:text-zinc-700" data-testid={`cutlist-rename-${list.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                {clLists.length > 1 && (
                                  <button onClick={() => setClDeleteId(list.id)} className="p-1.5 text-zinc-300 hover:text-red-500" data-testid={`cutlist-delete-${list.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      {clLists.length < 15 && (
                        <div className="flex items-center gap-2 mt-2">
                          <Input value={clNewName} onChange={(e) => setClNewName(e.target.value)} placeholder="New cut list name…" maxLength={40}
                            onKeyDown={async (e) => { if (e.key === 'Enter' && clNewName.trim()) { try { await axios.post(`${API}/cut-lists`, { name: clNewName.trim() }); setClNewName(""); await loadCutLists(); toast.success("Cut list added"); } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } } }}
                            className="h-9 text-sm rounded-none border-zinc-300 flex-1" data-testid="cutlist-new-name" />
                          <button onClick={async () => { if (!clNewName.trim()) return; try { await axios.post(`${API}/cut-lists`, { name: clNewName.trim() }); setClNewName(""); await loadCutLists(); toast.success("Cut list added"); } catch (er) { toast.error(er.response?.data?.detail || "Failed"); } }}
                            className="h-9 px-3 bg-zinc-800 text-white hover:bg-zinc-700 flex items-center gap-1 text-xs font-bold" data-testid="cutlist-add-btn"><Plus className="w-3.5 h-3.5" />Add</button>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mb-1.5">Show on this device</p>
                      {[{ id: 'all', name: 'All cut lists (stacked & collapsible)' }, ...clLists].map(opt => {
                        const match = clDisplay === opt.id;
                        return (
                          <button key={opt.id} onClick={() => { localStorage.setItem('active_cut_list', opt.id); setClDisplay(opt.id); window.dispatchEvent(new Event('cutlist-display-changed')); toast.success(opt.id === 'all' ? "Showing all cut lists" : `Showing only "${opt.name}"`); }}
                            className={`w-full flex items-center justify-between p-2.5 border mb-1.5 transition-colors ${match ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                            data-testid={`cutlist-display-${opt.id}`}>
                            <span className="text-sm font-medium text-zinc-800 truncate">{opt.name}</span>
                            {match && <span className="text-xs font-bold text-orange-600 flex-shrink-0">Current</span>}
                          </button>
                        );
                      })}
                      <p className="text-[11px] text-zinc-400 mt-1">This choice only applies to this device. Cut lists themselves are shared across your whole account.</p>
                    </div>
                  </>
                )}
              </div>
            )}


            {/* Subscription (own account) */}
            {section === "subscription" && (
              <div className="space-y-4">
                <BackButton />
                {!isOwnerDevice ? (
                  <div className="p-4 border border-zinc-200 bg-zinc-50 space-y-2" data-testid="subscription-locked-card">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Subscription</p>
                    <p className="text-sm text-zinc-700 font-semibold">{ownerLabelUA ? deviceLabel({ user_agent: ownerLabelUA, model: ownerModel }) : "Another device"} controls this account</p>
                    <p className="text-xs text-zinc-400">Subscription and billing settings are only available on the Owner device.</p>
                  </div>
                ) : mySub === null ? (
                  <p className="text-sm text-zinc-400">Loading…</p>
                ) : mySub.access === "offline" ? (
                  <p className="text-sm text-zinc-500">Couldn't reach the server. Check your connection and try again.</p>
                ) : (
                  <>
                    <div className="p-4 bg-zinc-50 border border-zinc-200" data-testid="my-subscription-status">
                      <p className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Status</p>
                      <p className="text-base font-semibold text-zinc-800">
                        {user?.is_admin || mySub.access === "free" ? "Free Access" :
                          mySub.access === "subscribed" ? `Subscribed — ${mySub.plan === "yearly" ? "Yearly ($180/yr)" : "Monthly ($20/mo)"}` :
                          mySub.access === "trial" ? `Free Trial — ${mySub.trial_days_left} day${mySub.trial_days_left === 1 ? "" : "s"} left` :
                          "Not subscribed"}
                      </p>
                      {mySub.access === "subscribed" && mySub.current_period_end && (
                        mySub.cancel_at_period_end ? (
                          <p className="text-xs font-bold text-red-600 mt-1" data-testid="sub-lockout-date">Subscription canceled — you'll be locked out of the app on {new Date(mySub.current_period_end).toLocaleDateString()}</p>
                        ) : (
                          <p className="text-xs text-zinc-400 mt-1">Renews {new Date(mySub.current_period_end).toLocaleDateString()}</p>
                        )
                      )}
                      {mySub.discount_percent > 0 && mySub.access !== "free" && (
                        <p className="text-xs font-bold text-green-700 mt-1">{mySub.discount_percent}% discount on your account</p>
                      )}
                    </div>
                    {mySub.access === "subscribed" && !mySub.cancel_at_period_end && (
                      subCancelConfirm ? (
                        <div className="p-3 border border-red-200 bg-red-50 space-y-2" data-testid="cancel-sub-confirm">
                          <p className="text-xs text-red-700">Cancel your subscription? You'll keep full access until {mySub.current_period_end ? new Date(mySub.current_period_end).toLocaleDateString() : "the end of your billing period"}, then the app locks until you resubscribe.</p>
                          <div className="flex gap-2">
                            <button onClick={async () => {
                              try {
                                await axios.post(`${API}/billing/cancel`);
                                const { data } = await axios.get(`${API}/billing/status`);
                                setMySub(data); setSubCancelConfirm(false);
                                window.dispatchEvent(new Event('billing-changed'));
                                toast.success("Subscription canceled — access continues until the period ends");
                              } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
                            }}
                              className="flex-1 py-2 text-xs font-bold bg-red-600 text-white hover:bg-red-700" data-testid="cancel-sub-yes">
                              Yes, Cancel
                            </button>
                            <button onClick={() => setSubCancelConfirm(false)} className="flex-1 py-2 text-xs font-bold border border-zinc-300 text-zinc-600 hover:bg-zinc-50" data-testid="cancel-sub-no">
                              Keep Subscription
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => setSubCancelConfirm(true)}
                          className="w-full py-2 text-xs font-bold border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                          data-testid="cancel-subscription-btn">
                          Cancel Subscription
                        </button>
                      )
                    )}
                    {mySub.access === "subscribed" && mySub.cancel_at_period_end && (
                      <button onClick={async () => {
                        try {
                          await axios.post(`${API}/billing/resume`);
                          const { data } = await axios.get(`${API}/billing/status`);
                          setMySub(data);
                          window.dispatchEvent(new Event('billing-changed'));
                          toast.success("Subscription resumed");
                        } catch (e) { toast.error(e.response?.data?.detail || "Could not resume"); }
                      }}
                        className="w-full py-2 text-xs font-bold border border-green-400 text-green-700 hover:bg-green-50 transition-colors"
                        data-testid="resume-subscription-btn">
                        Resume Subscription (undo cancellation)
                      </button>
                    )}
                    {(mySub.access === "trial" || mySub.access === "locked") && (
                      <div className="space-y-2">
                        {[["monthly", 20, "/month"], ["yearly", 180, "/year"]].map(([plan, base, per]) => {
                          const p = Math.round(base * (100 - (mySub.discount_percent || 0))) / 100;
                          return (
                            <button key={plan} onClick={async () => {
                              try {
                                const { data } = await axios.post(`${API}/billing/checkout`, { plan, origin_url: window.location.origin });
                                window.location.href = data.checkout_url;
                              } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
                            }}
                              className="w-full flex items-center justify-between p-3 border border-zinc-300 hover:border-orange-500 hover:bg-orange-50 transition-colors"
                              data-testid={`settings-subscribe-${plan}`}>
                              <span className="text-sm font-medium text-zinc-800 capitalize">{plan}</span>
                              <span className="text-sm font-bold text-zinc-900">${p}{per}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {/* Billing details: payment method, totals & all transactions */}
                    {mySubHistory === null ? (
                      <p className="text-xs text-zinc-400">Loading billing details…</p>
                    ) : (
                      <>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Payment Method</p>
                        <div className="p-3 border border-zinc-200 bg-zinc-50 space-y-2" data-testid="sub-payment-method">
                          {mySubHistory.payment_method ? (
                            <p className="text-sm font-semibold text-zinc-800 capitalize">
                              {mySubHistory.payment_method.brand || "Card"} •••• {mySubHistory.payment_method.last4}
                              {mySubHistory.payment_method.exp_month ? <span className="text-xs font-normal text-zinc-400"> — expires {mySubHistory.payment_method.exp_month}/{mySubHistory.payment_method.exp_year}</span> : null}
                            </p>
                          ) : (
                            <p className="text-xs text-zinc-400">No payment method on file yet.</p>
                          )}
                          {(mySubHistory.payment_method || mySubHistory.transactions.length > 0) && (
                            <button onClick={async () => {
                              try {
                                const { data } = await axios.post(`${API}/billing/portal`, { origin_url: window.location.origin });
                                window.location.href = data.url;
                              } catch (e) { toast.error(e.response?.data?.detail || "Could not open billing portal"); }
                            }}
                              className="w-full py-2 text-xs font-bold border border-zinc-300 text-zinc-700 hover:bg-zinc-100 transition-colors" data-testid="change-payment-method-btn">
                              Change Payment Method
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2" data-testid="sub-billing-totals">
                          <div className="p-3 border border-zinc-200 bg-zinc-50 text-center">
                            <p className="text-lg font-bold text-zinc-900" data-testid="sub-total-paid">${(mySubHistory.total_paid || 0).toFixed(2)}</p>
                            <p className="text-[10px] uppercase tracking-wider text-zinc-400">Total Paid</p>
                          </div>
                          <div className="p-3 border border-zinc-200 bg-zinc-50 text-center">
                            <p className={`text-lg font-bold ${(mySubHistory.total_refunded || 0) > 0 ? 'text-red-600' : 'text-zinc-900'}`} data-testid="sub-total-refunded">${(mySubHistory.total_refunded || 0).toFixed(2)}</p>
                            <p className="text-[10px] uppercase tracking-wider text-zinc-400">Total Refunded</p>
                          </div>
                        </div>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Transactions</p>
                        <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-52 overflow-y-auto" data-testid="sub-transactions">
                          {mySubHistory.transactions.length === 0 && (
                            <p className="p-3 text-xs text-zinc-400">No payments yet.</p>
                          )}
                          {mySubHistory.transactions.map((t, i) => (
                            <div key={i} className="p-3 flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-zinc-800">{new Date(t.date).toLocaleDateString()}</p>
                                <p className="text-[10px] text-zinc-400 capitalize">{t.card_brand ? `${t.card_brand} •••• ${t.card_last4}` : t.description}</p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <p className="text-xs font-bold text-zinc-900">${t.amount.toFixed(2)}</p>
                                {t.refunded > 0 && <p className="text-[10px] font-bold text-red-600">-${t.refunded.toFixed(2)} refunded</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                        {mySubHistory.member_since && (
                          <p className="text-[11px] text-zinc-400" data-testid="sub-member-since">Subscriber since {new Date(mySubHistory.member_since).toLocaleDateString()}</p>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Viewable Only */}
            {/* Owner: devices & view-only control */}
            {section === "owner" && (
              <div className="space-y-3">
                {!ownerSelected && <BackButton />}
                {!isOwnerDevice ? (
                  <div className="p-4 border border-zinc-200 bg-zinc-50 space-y-2" data-testid="owner-locked-card">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Account Owner</p>
                    <p className="text-sm text-zinc-700 font-semibold">{ownerLabelUA ? deviceLabel({ user_agent: ownerLabelUA, model: ownerModel }) : "Another device"}</p>
                    <p className="text-xs text-zinc-400">Device management, view-only page control, ownership transfer, and account deletion are only available on the Owner device.</p>
                  </div>
                ) : ownerSelected ? (
                  <>
                    <button onClick={() => setOwnerSelected(null)} className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700 mb-1" data-testid="owner-device-back">
                      <ChevronRight className="w-4 h-4 rotate-180" />
                      <span>All Devices</span>
                    </button>
                    <div className="p-3 border border-zinc-200 bg-zinc-50">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-zinc-800">{deviceLabel(ownerSelected)}</p>
                        {ownerSelected.is_owner && <span className="text-[9px] font-bold uppercase tracking-wider bg-orange-600 text-white px-1.5 py-0.5">Owner</span>}
                        {ownerSelected.device_id === myDeviceId && <span className="text-[9px] font-bold uppercase tracking-wider bg-zinc-700 text-white px-1.5 py-0.5">This Device</span>}
                      </div>
                      {deviceDetail(ownerSelected) && <p className="text-[11px] text-zinc-500 mt-1">{deviceDetail(ownerSelected)}</p>}
                      {ownerSelected.last_seen && <p className="text-[11px] text-zinc-400 mt-1">Last active {new Date(ownerSelected.last_seen).toLocaleDateString()} {new Date(ownerSelected.last_seen).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>}
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400">Viewable Only Pages</p>
                    <p className="text-xs text-zinc-400">Checked pages become view-only on that device — nothing can be added, edited, or deleted there. The device applies changes within seconds while online.</p>
                    {[["calculator", "Calculator"], ["cutlist", "Cut List"], ["lumber", "Lumber"], ["orders", "Orders"]].map(([page, label]) => {
                      const checked = (ownerSelected.viewonly_pages || []).includes(page);
                      return (
                        <button key={page} onClick={() => toggleDeviceViewonly(ownerSelected, page, label)}
                          className={`w-full flex items-center justify-between p-3 border transition-colors ${checked ? 'border-orange-500 bg-orange-50' : 'border-zinc-200 hover:bg-zinc-50'}`}
                          data-testid={`owner-viewonly-${page}`}>
                          <span className="text-sm font-medium text-zinc-800">{label}</span>
                          <span className={`w-5 h-5 border-2 flex items-center justify-center ${checked ? 'bg-orange-600 border-orange-600' : 'border-zinc-300'}`}>
                            {checked && <Check className="w-3.5 h-3.5 text-white" />}
                          </span>
                        </button>
                      );
                    })}
                    {!ownerSelected.is_owner && (
                      <button onClick={() => setTransferTarget(ownerSelected)}
                        className="w-full py-2.5 text-sm font-bold border border-orange-300 text-orange-700 hover:bg-orange-50 flex items-center justify-center gap-2" data-testid="transfer-ownership-btn">
                        <Shield className="w-4 h-4" /> Transfer Ownership To This Device
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-sm text-zinc-500">{ownerLoading ? "Loading devices…" : "Tap a device to control its view-only pages or transfer ownership."}</p>
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {ownerDevices.map(d => (
                        <button key={d.device_id} onClick={() => setOwnerSelected(d)}
                          className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-zinc-50" data-testid={`owner-device-${d.device_id}`}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="text-sm font-semibold text-zinc-800">{deviceLabel(d)}</p>
                              {d.is_owner && <span className="text-[9px] font-bold uppercase tracking-wider bg-orange-600 text-white px-1.5 py-0.5">Owner</span>}
                              {d.device_id === myDeviceId && <span className="text-[9px] font-bold uppercase tracking-wider bg-zinc-700 text-white px-1.5 py-0.5">This Device</span>}
                            </div>
                            {deviceDetail(d) && <p className="text-[10px] text-zinc-500">{deviceDetail(d)}</p>}
                            <p className="text-[11px] text-zinc-400">
                              {d.last_seen ? `Last active ${new Date(d.last_seen).toLocaleDateString()}` : ""}
                              {(d.viewonly_pages || []).length > 0 ? ` · ${d.viewonly_pages.length} page${d.viewonly_pages.length === 1 ? '' : 's'} view-only` : ""}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-zinc-300 flex-shrink-0" />
                        </button>
                      ))}
                      {!ownerLoading && ownerDevices.length === 0 && (
                        <p className="p-3 text-xs text-zinc-400">No devices found yet.</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}


            {/* Print History */}
            {section === "prints" && (
              <div className="space-y-3">
                <BackButton />
                {printHistory.length === 0 ? (
                  <p className="text-sm text-zinc-400 text-center py-6">No prints yet</p>
                ) : (
                  <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-[300px] overflow-y-auto">
                    {printHistory.map(p => (
                      <div key={p.id} className="flex items-center justify-between p-3 hover:bg-zinc-50">
                        <button onClick={() => viewPrint(p.id)} className="flex-1 text-left">
                          <p className="text-sm font-medium text-zinc-800">{new Date(p.date).toLocaleDateString()} {new Date(p.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                          <p className="text-xs text-zinc-400">{p.items?.length || 0} items{p.totals?.actual_time ? ` \u00B7 ${p.totals.actual_time}` : ''}</p>
                        </button>
                        <button onClick={() => setDeletePrintId(p.id)} className="p-1.5 text-zinc-300 hover:text-red-500 transition-colors" data-testid={`delete-print-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Cut List History */}
            {section === "cut-history" && (
              <div className="space-y-3">
                <BackButton />
                <Input value={cutHistSearch} onChange={(e) => setCutHistSearch(e.target.value)}
                  placeholder="Search component, pallet type, date…"
                  className="rounded-none border-zinc-300 h-9 text-sm"
                  data-testid="cut-history-search" />
                {(() => {
                  const noop = () => {};
                  const fmtEst = (minutes) => { if (minutes < 1) return `${Math.round(minutes * 60)} seconds`; if (minutes >= 60) { const h = Math.floor(minutes / 60); const m = Math.round(minutes % 60); return m > 0 ? `${h}h ${m}m` : `${h}h`; } return `${minutes.toFixed(1)} min`; };
                  const getElapsed = (i) => i.stopwatch_elapsed || 0;
                  const fmtSw = (s) => { const secs = Math.max(0, Math.floor(s || 0)); const h = Math.floor(secs/3600); const m = Math.floor((secs%3600)/60); const sc = secs%60; return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}` : `${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}`; };
                  const dateStrings = (ds) => { if (!ds) return ''; const d = new Date(String(ds).includes('T') ? ds : ds + 'T12:00:00'); if (isNaN(d.getTime())) return String(ds); return [String(ds), d.toLocaleDateString(), d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), d.toLocaleDateString('en-US', { weekday: 'long' }), d.toLocaleDateString('en-US', { weekday: 'short' }), `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`, `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`, `${d.getMonth() + 1}/${d.getDate()}`, `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`, `${d.getMonth() + 1}/${d.getFullYear()}`, String(d.getFullYear())].join(' '); };
                  const haystack = (i) => [
                    i.name || '', i.pallet_type_name || '', i.notes || '', i.extras || '',
                    String(i.total || ''), String(i.original_total || ''), String(i.per_pallet || ''), String(i.pallet_quantity || ''),
                    [i.thickness, i.height, i.length].filter(v => v != null && v !== '').join(' '),
                    i.thickness != null && i.height != null && i.length != null ? `${i.thickness}x${i.height}x${i.length} ${i.thickness} x ${i.height} x ${i.length}` : '',
                    i.notched ? 'notched notch' : '',
                    i.skids ? `${i.skids} skids skid` : '',
                    (i.lumber || []).map(l => l ? `${l.thickness || ''} ${l.width || ''} ${l.length || ''} ${l.description || ''} ${l.thickness && l.length ? `${l.thickness}x${l.width || ''}x${l.length}` : ''}` : '').join(' '),
                    (i.lumber_custom_species || []).filter(Boolean).join(' '),
                    (i.custom_lumber || []).map(cl => `${cl.thickness || ''} ${cl.width || ''} ${cl.length || ''} ${cl.species || ''}`).join(' '),
                    dateStrings(i.cut_date),
                  ].join(' ').toLowerCase();
                  const query = cutHistSearch.trim().toLowerCase();
                  const tokens = query.split(/\s+/).filter(Boolean);
                  let filtered = cutHistory;
                  let isFuzzy = false;
                  if (tokens.length > 0) {
                    filtered = cutHistory.filter(i => { const h = haystack(i); return tokens.every(t => h.includes(t)); });
                    if (filtered.length === 0) {
                      isFuzzy = true;
                      filtered = cutHistory
                        .map(i => ({ i, s: fuzzyScore(query, haystack(i)) }))
                        .filter(({ s }) => s >= 0.45)
                        .sort((a, b) => b.s - a.s)
                        .map(({ i }) => i);
                    }
                  }
                  if (cutHistory.length === 0) return <p className="text-sm text-zinc-400 text-center py-6" data-testid="cut-history-empty">No components in history yet. Use the 3-dots menu on a cut list component to move it here.</p>;
                  if (filtered.length === 0) return <p className="text-sm text-zinc-400 text-center py-6" data-testid="cut-history-no-results">Nothing in history even close to &quot;{cutHistSearch}&quot;</p>;
                  const sorted = isFuzzy ? filtered : [...filtered].sort((a, b) => String(b.history_moved_at || b.cut_date || '').localeCompare(String(a.history_moved_at || a.cut_date || '')));
                  return (
                    <div className="space-y-3 max-h-[440px] overflow-y-auto pr-0.5">
                      {isFuzzy && (
                        <p className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200" data-testid="cut-history-fuzzy-label">
                          No exact match — closest matches:
                        </p>
                      )}
                      {sorted.map((i, hIdx) => (
                        <div key={i.id} data-testid={`cut-hist-item-${i.id}`}>
                          <CutListItem
                            item={i} idx={hIdx} dragIdx={null} tick={0} reorderMode={false}
                            formatTime={fmtEst} formatStopwatch={fmtSw} getElapsed={getElapsed}
                            toggleCheck={noop} removeFromList={noop} moveItem={noop} toggleSavedForLater={noop}
                            updateItemCustomQty={noop} updateItemStopwatch={noop}
                            startTimer={noop} stopTimer={noop} resetTimer={noop}
                            editingIdx={null} setEditingIdx={noop} customQty="" setCustomQty={noop}
                            editingTimeId={null} setEditingTimeId={noop} customTime="" setCustomTime={noop}
                            readOnly={true} onDeleteHistory={() => setDeleteCutHistId(i.id)} onRestoreHistory={() => restoreCutHist(i.id)}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sign Out Confirmation */}
      {/* Delete cut list confirmation */}
      <AlertDialog open={!!clDeleteId} onOpenChange={(open) => { if (!open) setClDeleteId(null); }}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Delete Cut List?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This deletes the cut list <strong>{clLists?.find(l => l.id === clDeleteId)?.name}</strong>. It must be empty — if it still has components in it, the delete will be blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={async () => { try { await axios.delete(`${API}/cut-lists/${clDeleteId}`); await loadCutLists(); if (localStorage.getItem('active_cut_list') === clDeleteId) { localStorage.setItem('active_cut_list', 'all'); setClDisplay('all'); window.dispatchEvent(new Event('cutlist-display-changed')); } toast.success("Cut list deleted"); } catch (er) { toast.error(er.response?.data?.detail || "Failed to delete"); } setClDeleteId(null); }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="cutlist-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="logout-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              Sign Out?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              Are you sure you want to sign out?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="cancel-logout-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowLogoutConfirm(false); setOpen(false); handleLogout(); }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-none"
              data-testid="confirm-logout-btn"
            >
              Sign Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Print Confirmation */}
      <AlertDialog open={!!deletePrintId} onOpenChange={(open) => { if (!open) setDeletePrintId(null); }}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="delete-print-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              Delete Print?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              Are you sure you want to delete this saved print? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="cancel-delete-print-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { deletePrint(deletePrintId); setDeletePrintId(null); }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-none"
              data-testid="confirm-delete-print-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Cut History Confirmation */}
      <AlertDialog open={!!deleteCutHistId} onOpenChange={(open) => { if (!open) setDeleteCutHistId(null); }}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="delete-cut-hist-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Delete Component?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">Are you sure you want to permanently delete this component from your Cut List History? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { deleteCutHistItem(deleteCutHistId); setDeleteCutHistId(null); }} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-delete-cut-hist-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Account Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={(v) => { if (!v) { setShowDeleteConfirm(false); setDeletePassword(""); } }}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="delete-account-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              <AlertTriangle className="w-5 h-5" /> Delete Account?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              This will delete your account. Your data (pallet types, cut lists, orders, print history) is held for 10 days — if you change your mind, just log back in within 10 days and everything is restored. After 10 days it's permanently erased. Enter your password to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
              placeholder="Enter your password" className="rounded-none border-zinc-300" data-testid="delete-account-password-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300" onClick={() => setDeletePassword("")} data-testid="cancel-delete-account-btn">Cancel</AlertDialogCancel>
            <Button onClick={handleDeleteAccount} disabled={deleting || !deletePassword}
              className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="confirm-delete-account-btn">
              {deleting ? "Deleting..." : "Delete My Account"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Ownership Confirmation */}
      <AlertDialog open={!!transferTarget} onOpenChange={(v) => { if (!v) setTransferTarget(null); }}>
        <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="transfer-ownership-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-zinc-900 flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
              <Shield className="w-5 h-5 text-orange-600" /> Transfer Ownership?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-600">
              {transferTarget ? `"${deviceLabel(transferTarget)}" will become the Owner of this account. This device will lose the Owner controls — view-only page control, ownership transfer, and account deletion will only work on the new Owner device.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-zinc-300" data-testid="cancel-transfer-ownership-btn">Cancel</AlertDialogCancel>
            <Button onClick={doTransferOwnership} disabled={transferring}
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-none" data-testid="confirm-transfer-ownership-btn">
              {transferring ? "Transferring..." : "Transfer Ownership"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
